import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowUpRight, Sparkles, TrendingUp, Users as UsersIcon, MessageSquare, Lock } from "lucide-react";

const platformColor = {
  fanvue: "text-emerald-400",
  telegram: "text-sky-400",
  instagram: "text-rose-400",
  x: "text-white",
};

export default function Dashboard() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/analytics/overview");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const seedDemo = async () => {
    await api.post("/dev/seed-demo");
    toast.success("Demo data ready");
    load();
  };

  if (loading) {
    return <div className="p-12 text-white/40 font-serif text-2xl">loading…</div>;
  }

  const d = data || {};
  const isEmpty = (d.fans_count || 0) === 0;

  return (
    <div className="p-8 lg:p-12 space-y-10 fade-up">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-white/40">{t("dashboard")}</div>
          <h1 className="font-serif text-5xl mt-2 leading-none">
            Tonight she made
            <span className="italic text-fuchsia-400"> them feel something.</span>
          </h1>
        </div>
        {isEmpty && (
          <Button onClick={seedDemo} data-testid="btn-seed-demo" className="bg-fuchsia-500 hover:bg-fuchsia-400 text-white rounded-none px-5 py-2 text-xs uppercase tracking-widest">
            <Sparkles size={14} className="mr-2" />
            {t("seed_demo")}
          </Button>
        )}
      </header>

      {/* Bento metrics */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <Card className="md:col-span-7 row-span-2" data-testid="card-revenue">
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">
                {t("total_revenue")}{d.fanvue_earnings != null ? " · Fanvue" : ""}
              </div>
              <div className="font-serif text-7xl tabular-nums mt-2 leading-none text-emerald-400">
                ${((d.fanvue_earnings != null ? d.fanvue_earnings : d.total_revenue) || 0).toFixed(2)}
              </div>
              {d.fanvue_earnings != null && d.total_revenue > 0 && (
                <div className="text-[10px] text-white/30 mt-1 tabular-nums">
                  z toho cez dash: ${(d.total_revenue || 0).toFixed(2)}
                </div>
              )}
            </div>
            <TrendingUp className="text-emerald-400/40" size={28} strokeWidth={1.5} />
          </div>
          <div className="grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">{t("total_tips")}</div>
              <div className="font-serif text-2xl tabular-nums mt-1">${(d.total_tips || 0).toFixed(2)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">{t("nsfw_unlocked")}</div>
              <div className="font-serif text-2xl tabular-nums mt-1">{d.nsfw_unlocked_count || 0}</div>
            </div>
          </div>
        </Card>

        <Card className="md:col-span-5" data-testid="card-fans">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-white/40">{t("fans_count")}</div>
            <UsersIcon size={16} className="text-white/30" strokeWidth={1.5} />
          </div>
          <div className="font-serif text-5xl tabular-nums mt-2">{d.fans_count || 0}</div>
        </Card>

        <Card className="md:col-span-5" data-testid="card-messages">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-white/40">{t("messages_count")}</div>
            <MessageSquare size={16} className="text-white/30" strokeWidth={1.5} />
          </div>
          <div className="font-serif text-5xl tabular-nums mt-2">{d.messages_count || 0}</div>
        </Card>
      </div>

      {/* Two columns: top fans + recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">{t("top_fans")}</h2>
            <Link to="/fans" className="text-xs text-white/40 hover:text-white flex items-center gap-1" data-testid="link-all-fans">
              all <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="border border-white/5 divide-y divide-white/5">
            {(d.top_fans || []).length === 0 && (
              <div className="px-5 py-10 text-center text-white/30 text-sm">{t("no_fans_yet")}</div>
            )}
            {(d.top_fans || []).map((f) => (
              <Link
                key={f.id}
                to={`/inbox?fan=${f.id}`}
                className="px-5 py-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors group"
                data-testid={`top-fan-${f.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`text-[9px] uppercase tracking-widest ${platformColor[f.platform]}`}>{f.platform}</div>
                  <div className="font-serif text-lg truncate">{f.name}</div>
                  {f.nsfw_unlocked && <Lock size={11} className="text-rose-400 -rotate-12" />}
                </div>
                <div className="font-serif text-lg tabular-nums text-emerald-400">
                  ${(f.spend_total || 0).toFixed(2)}
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">{t("recent_activity")}</h2>
            <Link to="/inbox" className="text-xs text-white/40 hover:text-white flex items-center gap-1" data-testid="link-inbox">
              inbox <ArrowUpRight size={12} />
            </Link>
          </div>
          <div className="border border-white/5 divide-y divide-white/5">
            {(d.recent_activity || []).length === 0 && (
              <div className="px-5 py-10 text-center text-white/30 text-sm">{t("no_messages")}</div>
            )}
            {(d.recent_activity || []).slice(0, 8).map((m) => (
              <div key={m.id} className="px-5 py-3 text-sm flex items-start gap-3">
                <div className={`text-[9px] uppercase tracking-widest pt-1 shrink-0 ${platformColor[m.platform]}`}>
                  {m.platform.slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white/80 truncate">{m.text}</div>
                  <div className="text-[10px] text-white/30 mt-0.5">
                    {m.from_creator ? "she sent" : "he sent"} · {new Date(m.created_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isEmpty && (
        <div className="border border-fuchsia-500/20 bg-fuchsia-500/[0.04] p-6">
          <div className="font-serif italic text-2xl text-fuchsia-200">First time?</div>
          <p className="text-sm text-white/60 mt-2 max-w-xl">{t("seed_demo_hint")}</p>
        </div>
      )}
    </div>
  );
}

function Card({ children, className = "", ...props }) {
  return (
    <div
      className={`bg-[rgb(18,20,28)] border border-white/5 p-6 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}
